const PARAM_KEYS_BY_VEHICLE_STEP = [
  null,
  [],
  ["brand"],
  ["brand", "model"],
  ["brand", "model", "year"],
  ["brand", "model", "year", "bauform"],
  ["brand", "model", "year", "bauform", "fuel"],
  ["brand", "model", "year", "bauform", "fuel", "variant"],
  ["brand", "model", "year", "bauform", "fuel", "variant", "kw"],
  [
    "brand",
    "model",
    "year",
    "bauform",
    "fuel",
    "variant",
    "kw",
    "gearbox",
  ],
  [
    "brand",
    "model",
    "year",
    "bauform",
    "fuel",
    "variant",
    "kw",
    "gearbox",
    "doors",
  ],
];

const SELECTION_PARAM_BY_VEHICLE_STEP = [
  null,
  "brand",
  "model",
  "year",
  "bauform",
  "fuel",
  "variant",
  "kw",
  "gearbox",
  "doors",
  "modelType",
];

const VALUE_KEY_BY_VEHICLE_STEP = [
  null,
  "id",
  "id",
  "id",
  "id",
  "id",
  "name",
  "id",
  "id",
  "id",
  "id",
];

const REQUIRED_MESSAGE_BY_VEHICLE_STEP = [
  null,
  "Keine gültige Automarke",
  "Kein gültiges Modell",
  "Kein gültiges Baujahr",
  "Keine gültige Bauform",
  "Kein gültiger Kraftstoff",
  "Keine Variante gewählt",
  "Kein PS-Wert gewählt",
  "Kein Getriebe gewählt",
  "Keine Türanzahl eingegeben",
  "Kein Modelltyp ausgewählt",
];

const buildParams = (selections) => ({
  brand: selections?.selectedBrand?.id || "",
  model: selections?.selectedModel?.id || "",
  year: selections?.selectedYear?.id || "",
  bauform: selections?.selectedBauform?.id || "",
  fuel: selections?.selectedFuel?.id || "",
  variant: selections?.selectedVariant?.name || "",
  kw: selections?.selectedPs?.id || "",
  gearbox: selections?.selectedTransmission?.id || "",
  doors: selections?.selectedDoors?.id || "",
  modelType: selections?.selectedModelType?.id || "",
});

const normalizeOptions = (data) => (Array.isArray(data) ? data : []);

export const createVehicleFlow = ({
  api,
  setOptionsByStep,
  setInputsByStep,
  setSelectedByStep,
  setFilteredItems,
  showToast,
  stepOffset = 0,
  alwaysSkipVehicleSteps = [],
}) => {
  const alwaysSkipSet = new Set(alwaysSkipVehicleSteps);
  const toVehicleStep = (uiStep) => uiStep - stepOffset;
  const toUiStep = (vehicleStep) => vehicleStep + stepOffset;

  const loadOptions = async (params) => {
    try {
      const response = await fetch(`${api}/vehicleInfo/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });
      return await response.json();
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const loadVehicleStepOptions = async (vehicleStep, params = {}) => {
    const keys = PARAM_KEYS_BY_VEHICLE_STEP[vehicleStep] || [];
    const body = {};
    keys.forEach((key) => {
      if (params[key] !== undefined) body[key] = params[key];
    });

    const data = await loadOptions(body);
    const normalized = normalizeOptions(data);
    const finalData =
      vehicleStep === 3
        ? [...normalized].sort((a, b) => b.id - a.id)
        : normalized;

    const uiStep = toUiStep(vehicleStep);
    const setter = setOptionsByStep?.[uiStep - 1];
    if (typeof setter === "function") setter(finalData);
    return finalData;
  };

  const loadStepOptions = async (step, params = {}) =>
    loadVehicleStepOptions(toVehicleStep(step), params);

  const loadBrands = async () => loadVehicleStepOptions(1, {});

  const selectOptionForVehicleStep = (vehicleStep, option) => {
    const uiStep = toUiStep(vehicleStep);
    const setInput = setInputsByStep?.[uiStep - 1];
    const setSelected = setSelectedByStep?.[uiStep - 1];

    if (typeof setInput === "function") setInput(option?.name ?? "");
    if (typeof setSelected === "function")
      setSelected(option || { id: "", name: "" });
    if (typeof setFilteredItems === "function") setFilteredItems([]);
  };

  const resetFromStep = (startStep) => {
    const firstIdx = Math.max(0, startStep - 1);
    const kmIndex = stepOffset + 10;

    for (let i = firstIdx; i < (setSelectedByStep?.length || 0); i += 1) {
      const resetSelected = setSelectedByStep?.[i];
      if (typeof resetSelected !== "function") continue;
      if (i === kmIndex) resetSelected("");
      else resetSelected({ id: "", name: "" });
    }

    for (let i = firstIdx; i < (setInputsByStep?.length || 0); i += 1) {
      const resetInput = setInputsByStep?.[i];
      if (typeof resetInput === "function") resetInput("");
    }

    const startOptionsIndex = Math.max(1, startStep);
    for (let i = startOptionsIndex; i < (setOptionsByStep?.length || 0); i += 1) {
      const resetOptions = setOptionsByStep?.[i];
      if (typeof resetOptions === "function") resetOptions([]);
    }

    if (typeof setFilteredItems === "function") setFilteredItems([]);
  };

  const validateStep = (step, params) => {
    const key = SELECTION_PARAM_BY_VEHICLE_STEP[step];
    const message = REQUIRED_MESSAGE_BY_VEHICLE_STEP[step];
    if (!key || !message) return true;
    if (params[key]) return true;
    if (typeof showToast === "function") showToast(message, "error");
    return false;
  };

  const tryAutoSkip = async (startingVehicleStep, params) => {
    let current = startingVehicleStep;

    while (current >= 2 && current <= 10) {
      const dataArr = await loadVehicleStepOptions(current, params);
      const options = normalizeOptions(dataArr);
      if (options.length !== 1) break;

      const only = options[0];
      const paramKey = SELECTION_PARAM_BY_VEHICLE_STEP[current];
      const valueKey = VALUE_KEY_BY_VEHICLE_STEP[current];
      const onlyValue = only?.[valueKey];

      if (onlyValue == null) break;
      if (
        !alwaysSkipSet.has(current) &&
        String(onlyValue) === String(params[paramKey])
      )
        break;

      selectOptionForVehicleStep(current, only);
      params[paramKey] = onlyValue;
      current += 1;
    }

    return current;
  };

  const getNextStep = async ({ step, selections }) => {
    const vehicleStep = toVehicleStep(step);
    if (vehicleStep <= 0) return "STOP";
    const params = buildParams(selections);

    if (vehicleStep === 1) {
      if (!validateStep(1, params)) return "STOP";
      const finalVehicleStep = await tryAutoSkip(2, params);
      return finalVehicleStep == null ? "STOP" : toUiStep(finalVehicleStep);
    }

    if (vehicleStep >= 2 && vehicleStep <= 9) {
      if (!validateStep(vehicleStep, params)) return "STOP";
      const finalVehicleStep = await tryAutoSkip(vehicleStep + 1, params);
      return finalVehicleStep == null ? "STOP" : toUiStep(finalVehicleStep);
    }

    if (vehicleStep === 10) {
      if (!validateStep(10, params)) return "STOP";
      return toUiStep(11);
    }

    return "STOP";
  };

  const getBackStep = async ({ step, selections }) => {
    const vehicleStep = toVehicleStep(step);
    if (vehicleStep <= 1) return "STOP";
    const params = buildParams(selections);

    for (let target = vehicleStep - 1; target >= 2; target -= 1) {
      const options = await loadVehicleStepOptions(target, params);
      if (Array.isArray(options) && options.length > 1) {
        resetFromStep(toUiStep(target));
        return toUiStep(target);
      }
    }

    const brandStep = toUiStep(1);
    resetFromStep(brandStep);
    return brandStep;
  };

  return {
    getNextStep,
    getBackStep,
    loadBrands,
    resetFromStep,
    loadStepOptions,
  };
};
